import { useMemo } from "react";
import client from "libs/client";
import { useI18n } from "features/translation/context";
import { GameOptions } from "pages/productManagement/types";
import type { DynamicFilterOptions, FilterConfig } from "../../datagrid-configs/types";

export function useSubscriptionFilterOptions(filters: FilterConfig[]) {
	const { _ } = useI18n();
	const { data, isLoading: isFilterDataLoading } = client.getSubscriptionFilterData.useQuery({}, { retry: false });
	const { data: teamGroupsData, isLoading: isTeamGroupsLoading } = client.loadTeamGroups.useQuery({}, { retry: false });
	const { data: eyeballAddonsData, isLoading: isEyeballAddonsLoading } = client.getAllEyeballAddons.useQuery({}, { retry: false });

	const dynamicOptions = useMemo((): DynamicFilterOptions => {
		const options: DynamicFilterOptions = {};

		if (filters.some((f) => f.id === "product") && data?.data?.products) {
			options.product = data.data.products.map((item) => ({
				value: String(item.value),
				label: String(item.label),
			}));
		}

		if (filters.some((f) => f.id === "proClubId") && data?.data?.clubs) {
			options.proClubId = data.data.clubs.map((item) => ({
				value: Number(item.value),
				label: String(item.label),
			}));
		}

		if (filters.some((f) => f.id === "teamGroupId") && teamGroupsData?.data?.data) {
			options.teamGroupId = teamGroupsData.data.data
				.filter((item) => item.value != null)
				.map((item) => ({
					value: Number(item.value),
					label: String(item.label ?? item.value),
				}));
		}

		if (filters.some((f) => f.id === "gameOptions")) {
			const gameOptionsLabels: Record<GameOptions, string> = {
				[GameOptions.AI]: _("label.AI"),
				[GameOptions.STATISTICS_RATING]: _("label.statistics_rating"),
				[GameOptions.STATISTICS_PLAYERS]: _("label.statistics_player"),
				[GameOptions.STATISTICS_TEAMS]: _("label.statistics_team"),
				[GameOptions.EVENTS]: _("label.events"),
				[GameOptions.DOWNLOADABLE]: _("label.download"),
				[GameOptions.CLIPS]: _("label.clips"),
				[GameOptions.DOWNLOADABLE_GAMETAGS]: _("label.downloadable_gametags"),
				[GameOptions.PLAYER_STATS_SEARCH]: _("label.player_stats_search"),
			};

			options.gameOptions = Object.values(GameOptions).map((value) => ({
				value,
				label: gameOptionsLabels[value],
			}));
		}

		if (filters.some((f) => f.id === "eyeballAddons") && eyeballAddonsData?.data) {
			options.eyeballAddons = eyeballAddonsData.data.map((item) => ({
				value: String(item.value),
				label: String(item.label),
			}));
		}

		return options;
	}, [_, data, eyeballAddonsData, filters, teamGroupsData]);

	return {
		dynamicOptions,
		isLoading: isFilterDataLoading || isTeamGroupsLoading || isEyeballAddonsLoading,
		error: null,
	};
}

export default useSubscriptionFilterOptions;
